import { Modal, Stack } from "@mantine/core"
import { Button, EqualWidthGrid } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ProductSheetLanguage } from "@tfg/types/constants/language.constants"
import { Content } from "@tfg/types/schemas/communication-studio/components"
import { ComponentTypesEnum } from "@tfg/types/types/communication-studio/components.types"

import { queryClient } from "~/backend/queryClient"
import {
  getProductPageComponentsQueryKey,
  GetProductPageComponentsResponseDto,
  useUpdateProductPageComponentsContent,
} from "~/gen/api"
import { DynamicTextEditor } from "~/pages/Brand/CommunicationStudio/Content/DynamicTextEditor"
import { useOrganizationSettings } from "~/pages/Brand/CommunicationStudio/contexts/organizationSettings/useOrganizationSettings"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

interface Props {
  selectedComponent: GetProductPageComponentsResponseDto
  onClose: () => void
  isOpen: boolean
  defaultLanguage: ProductSheetLanguage
}

export const EditComponentModalV2 = ({
  selectedComponent,
  onClose,
  isOpen,
  defaultLanguage,
}: Props) => {
  const { t } = useTranslation("communicationStudio")
  const { displaySettingsId, organization } = useOrganizationSettings()
  const { mutate: updateProductPageComponentContent } =
    useUpdateProductPageComponentsContent({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getProductPageComponentsQueryKey(displaySettingsId ?? ""),
          })
          await queryClient.invalidateQueries({
            queryKey: ["organization", organization.id],
          })
          onClose()
          showSuccessNotification({
            title: t("tabs.content.editProductComponentModal.success.title"),
            message: t(
              "tabs.content.editProductComponentModal.success.message",
              {
                componentType: t(
                  `tabs.content.components.${selectedComponent.componentType}`
                ),
              }
            ),
          })
        },
        onError: (error: unknown) => {
          showAxiosError({
            error: error as Error,
            notificationTitle: t(
              "tabs.content.editProductComponentModal.error.title"
            ),
            notificationMessage: t(
              "tabs.content.editProductComponentModal.error.message",
              {
                componentType: t(
                  `tabs.content.components.${selectedComponent.componentType}`
                ),
              }
            ),
            showNotification: true,
          })
        },
      },
    })

  const [content, setContent] = useState<Content>(
    selectedComponent.content ?? {}
  )

  return (
    <Modal opened={isOpen} onClose={onClose} title="Edit Component">
      <Stack>
        <DynamicTextEditor
          value={content}
          onChange={setContent}
          defaultLanguage={defaultLanguage}
          contentType={
            selectedComponent.componentType === ComponentTypesEnum.BULLETED_LIST
              ? "list"
              : "text"
          }
        />
        <EqualWidthGrid>
          <Button fullWidth onClick={onClose} variant="secondary">
            {t("tabs.content.editProductComponentModal.cancelButton")}
          </Button>
          <Button
            fullWidth
            onClick={() => {
              updateProductPageComponentContent({
                data: {
                  productPageComponentId: selectedComponent.id,
                  content,
                  configurations: selectedComponent.configurations,
                },
              })
            }}
          >
            {t("tabs.content.editProductComponentModal.saveButton")}
          </Button>
        </EqualWidthGrid>
      </Stack>
    </Modal>
  )
}
